import apiClient from './api';
import type {
  User,
  UserProfile,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
} from '@/types';

const WECHAT_PENDING_TEST_START_KEY = 'free_test_pending_wechat_start';

export class AuthService {
  /**
   * 用户登录
   */
  static async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await apiClient.post('/v1/auth/phone-login', credentials);
    
    // 保存认证信息
    if (response.token) {
      apiClient.setAuthToken(response.token);
      localStorage.setItem('user_info', JSON.stringify(response.user));
    }
    
    return response;
  }

  static async sendSmsCode(phone: string, purpose: 'login' | 'bind_phone' = 'login'): Promise<{
    message: string;
    expiresIn: number;
    cooldown: number;
  }> {
    return apiClient.post('/v1/auth/sms/send', { phone, purpose });
  }

  static async bindPhone(phone: string, code: string): Promise<AuthResponse> {
    const response = await apiClient.post('/v1/auth/bind-phone', { phone, code });

    if (response.token) {
      apiClient.setAuthToken(response.token);
      localStorage.setItem('user_info', JSON.stringify(response.user));
    }

    return response;
  }

  /**
   * 用户注册
   */
  static async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await apiClient.post('/v1/auth/register', userData);
    
    // 保存认证信息
    if (response.token) {
      apiClient.setAuthToken(response.token);
      localStorage.setItem('user_info', JSON.stringify(response.user));
    }
    
    return response;
  }

  /**
   * 用户登出
   */
  static async logout(): Promise<void> {
    try {
      await apiClient.post('/v1/auth/logout');
    } catch (error) {
      // 即使服务器登出失败，也要清除本地数据
      console.warn('Server logout failed:', error);
    } finally {
      // 清除本地认证信息
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_info');
    }
  }

  /**
   * 刷新token
   */
  static async refreshToken(): Promise<AuthResponse> {
    const response = await apiClient.post('/v1/auth/refresh');
    
    if (response.token) {
      apiClient.setAuthToken(response.token);
      localStorage.setItem('user_info', JSON.stringify(response.user));
    }
    
    return response;
  }

  /**
   * 获取当前用户信息
   */
  static async getCurrentUser(): Promise<UserProfile> {
    return apiClient.get('/v1/auth/me');
  }

  /**
   * 更新用户资料
   */
  static async updateProfile(userData: Partial<UserProfile>): Promise<UserProfile> {
    const response = await apiClient.put('/v1/auth/profile', userData);
    
    // 更新本地用户信息
    localStorage.setItem('user_info', JSON.stringify(response));
    
    return response;
  }

  /**
   * 修改密码
   */
  static async changePassword(data: {
    currentPassword: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<void> {
    return apiClient.post('/v1/auth/change-password', data);
  }

  /**
   * 忘记密码
   */
  static async forgotPassword(email: string): Promise<{ message: string }> {
    return apiClient.post('/v1/auth/forgot-password', { email });
  }

  /**
   * 重置密码
   */
  static async resetPassword(data: {
    token: string;
    newPassword: string;
    confirmPassword: string;
  }): Promise<{ message: string }> {
    return apiClient.post('/v1/auth/reset-password', data);
  }

  /**
   * 验证邮箱
   */
  static async verifyEmail(token: string): Promise<{ message: string }> {
    return apiClient.post('/v1/auth/verify-email', { token });
  }

  /**
   * 重新发送验证邮件
   */
  static async resendVerificationEmail(): Promise<{ message: string }> {
    return apiClient.post('/v1/auth/resend-verification');
  }

  /**
   * 检查邮箱是否已存在
   */
  static async checkEmailExists(email: string): Promise<{ exists: boolean }> {
    return apiClient.get('/v1/auth/check-email', { email });
  }

  /**
   * 检查用户名是否已存在
   */
  static async checkUsernameExists(username: string): Promise<{ exists: boolean }> {
    return apiClient.get('/v1/auth/check-username', { username });
  }

  /**
   * 获取用户统计信息
   */
  static async getUserStatistics(userId?: string): Promise<{
    totalTests: number;
    averageScore: number;
    bestScore: number;
    improvementRate: number;
    testHistory: Array<{
      date: string;
      score: number;
      cefrLevel: string;
    }>;
    cefrDistribution: Record<string, number>;
    monthlyProgress: Array<{
      month: string;
      testsCount: number;
      averageScore: number;
    }>;
  }> {
    const url = userId ? `/v1/auth/statistics/${userId}` : '/v1/auth/statistics';
    return apiClient.get(url);
  }

  /**
   * 更新用户偏好设置
   */
  static async updatePreferences(preferences: {
    language?: 'zh' | 'en';
    theme?: 'light' | 'dark';
    notifications?: boolean;
    emailNotifications?: boolean;
    testReminders?: boolean;
  }): Promise<void> {
    return apiClient.put('/v1/auth/preferences', preferences);
  }

  /**
   * 获取用户偏好设置
   */
  static async getPreferences(): Promise<{
    language: 'zh' | 'en';
    theme: 'light' | 'dark';
    notifications: boolean;
    emailNotifications: boolean;
    testReminders: boolean;
  }> {
    return apiClient.get('/v1/auth/preferences');
  }

  /**
   * 删除用户账户
   */
  static async deleteAccount(password: string): Promise<{ message: string }> {
    return apiClient.delete('/v1/auth/account', { password });
  }

  /**
   * 上传用户头像
   */
  static async uploadAvatar(file: File): Promise<{ avatarUrl: string }> {
    return apiClient.upload('/v1/auth/avatar', file);
  }

  /**
   * 获取用户活动日志
   */
  static async getActivityLog(limit: number = 20): Promise<Array<{
    id: string;
    action: string;
    description: string;
    timestamp: string;
    ipAddress: string;
    userAgent: string;
  }>> {
    return apiClient.get('/v1/auth/activity-log', { limit });
  }

  /**
   * 启用两步验证
   */
  static async enableTwoFactor(): Promise<{
    qrCode: string;
    backupCodes: string[];
    secret: string;
  }> {
    return apiClient.post('/v1/auth/2fa/enable');
  }

  /**
   * 验证两步验证码
   */
  static async verifyTwoFactor(code: string): Promise<{ verified: boolean }> {
    return apiClient.post('/v1/auth/2fa/verify', { code });
  }

  /**
   * 禁用两步验证
   */
  static async disableTwoFactor(password: string): Promise<{ message: string }> {
    return apiClient.post('/v1/auth/2fa/disable', { password });
  }

  /**
   * 检查认证状态
   */
  static isAuthenticated(): boolean {
    const token = localStorage.getItem('auth_token');
    const userInfo = localStorage.getItem('user_info');
    return !!(token && userInfo);
  }

  /**
   * 获取本地用户信息
   */
  static getLocalUserInfo(): User | null {
    const userInfo = localStorage.getItem('user_info');
    return userInfo ? JSON.parse(userInfo) : null;
  }

  /**
   * 获取本地认证token
   */
  static getLocalAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  /**
   * 清除本地认证信息
   */
  static clearLocalAuth(): void {
    localStorage.removeItem('auth_token');
    localStorage.removeItem('user_info');
  }

  /**
   * 从URL参数初始化Token（用于小程序/APP WebView嵌入）
   * @returns 是否成功从URL获取并设置了Token
   */
  static initializeFromUrlToken(): boolean {
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const token = urlParams.get('token');
      const wechatLogin = urlParams.get('wechat_login');
      const sessionId = urlParams.get('session_id');

      if (token) {
        // 保存token到localStorage
        localStorage.setItem('auth_token', token);
        if (wechatLogin === 'success') {
          sessionStorage.setItem(
            WECHAT_PENDING_TEST_START_KEY,
            JSON.stringify({
              token,
              sessionId: sessionId || undefined,
              createdAt: Date.now(),
            })
          );
        }

        // 清除URL中的token参数（安全考虑）
        const url = new URL(window.location.href);
        url.searchParams.delete('token');
        window.history.replaceState({}, '', url.toString());

        console.log('[AuthService] Token从URL参数初始化成功');
        return true;
      }
      return false;
    } catch (error) {
      console.error('[AuthService] URL Token初始化失败:', error);
      return false;
    }
  }

  /**
   * 验证Token并获取用户信息（用于外部登录后的用户信息同步）
   */
  static async validateAndSyncUser(): Promise<User | null> {
    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return null;

      // 调用后端验证token并获取用户信息
      const response = await apiClient.get('/v1/auth/me');

      if (response) {
        // 保存用户信息
        localStorage.setItem('user_info', JSON.stringify(response));
        return response as User;
      }
      return null;
    } catch (error) {
      console.error('[AuthService] Token验证失败:', error);
      // Token无效，清除本地认证信息
      AuthService.clearLocalAuth();
      return null;
    }
  }
}

export default AuthService;
